import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { Loader2, Mail, TestTube, Save, Building2, Copy, CheckCircle2, ExternalLink, Upload, Trash2, Image as ImageIcon, Lock, Shield, Users as UsersIcon, Bell, Database, Wifi } from "lucide-react";
import { toast } from "sonner";
import { API, useAuth } from "@/context/AuthContext";
import NotificationPrefsPanel from "@/components/NotificationPrefsPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import AccessProfilesPanel from "@/components/AccessProfilesPanel";
import AccessUsersPanel from "@/components/AccessUsersPanel";
import DatabasePortabilityPanel from "@/components/DatabasePortabilityPanel";
import SynklyAccountsPanel from "@/components/SynklyAccountsPanel";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const PUBLIC_BASE = typeof window !== "undefined" ? window.location.origin : "";

const SettingsPage = () => {
  const { user, refreshUser, can } = useAuth();
  const canAccessProfiles = can("access", "profiles");
  const canAccessUsers = can("access", "users");
  const canAccess = canAccessProfiles || canAccessUsers;
  // Only admins see the database export/import tab (matches backend gate)
  const isAdmin = (user?.role || "").toLowerCase() === "admin";
  const [accessSubTab, setAccessSubTab] = useState(canAccessProfiles ? "perfis" : "usuarios");
  const [activeTab, setActiveTab] = useState("empresa");

  const TAB_TITLES = {
    empresa: "Empresa",
    smtp: "SMTP",
    synkly: "Synkly",
    notificacoes: "Notificações",
    interview: "Entrevistas",
    video: "Vídeo",
    acessos: "Acessos",
    backup: "Backup",
  };
  useDocumentTitle(`Configurações — ${TAB_TITLES[activeTab] || ""}`);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [testEmail, setTestEmail] = useState("");

  // Company info
  const [companyName, setCompanyName] = useState("");
  const [companySlug, setCompanySlug] = useState("");
  const [savingCompany, setSavingCompany] = useState(false);
  const [copiedCareers, setCopiedCareers] = useState(false);

  // Logo
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);

  const [smtp, setSmtp] = useState({
    host: "",
    port: 587,
    username: "",
    password: "",
    encryption: "AUTO",
    from_email: "",
    from_name: "",
  });
  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/settings`);
        setSmtp({ ...res.data.smtp, port: res.data.smtp.port || 587 });
      } catch {
        toast.error("Não foi possível carregar configurações");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Sync company fields when user is loaded
  useEffect(() => {
    if (user) {
      setCompanyName(user.company_name || "");
      setCompanySlug(user.company_slug || "");
    }
  }, [user]);

  const handleSaveCompany = async () => {
    if (!companyName.trim()) {
      toast.error("Informe o nome da empresa");
      return;
    }
    setSavingCompany(true);
    try {
      await axios.put(`${API}/auth/me`, {
        company_name: companyName.trim(),
        company_slug: companySlug.trim() || undefined,
      });
      await refreshUser();
      toast.success("Empresa atualizada");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSavingCompany(false);
    }
  };

  const handleLogoSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo muito grande (máx. 2 MB)");
      return;
    }
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("logo", file);
      await axios.post(`${API}/settings/company/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await refreshUser();
      toast.success("Logo atualizada");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível enviar a logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleLogoRemove = async () => {
    setUploadingLogo(true);
    try {
      await axios.delete(`${API}/settings/company/logo`);
      await refreshUser();
      toast.success("Logo removida");
    } catch {
      toast.error("Não foi possível remover");
    } finally {
      setUploadingLogo(false);
    }
  };

  const careersUrl =
    user?.company_slug
      ? `${typeof window !== "undefined" ? window.location.origin : ""}/carreiras/${user.company_slug}`
      : "";

  const copyCareers = async () => {
    if (!careersUrl) return;
    try {
      await navigator.clipboard.writeText(careersUrl);
      setCopiedCareers(true);
      toast.success("Link copiado!");
      setTimeout(() => setCopiedCareers(false), 2000);
    } catch {
      toast.error("Não foi possível copiar");
    }
  };

  const updateSmtp = (key, val) => setSmtp((s) => ({ ...s, [key]: val }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        smtp: { ...smtp, port: Number(smtp.port) || 587 },
      };
      const res = await axios.put(`${API}/settings`, payload);
      setSmtp({ ...res.data.smtp, port: res.data.smtp.port || 587 });
      toast.success("Configurações salvas");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const handleTestSmtp = async () => {
    if (!testEmail) {
      toast.error("Informe um e-mail de destino para o teste");
      return;
    }
    setTestingSmtp(true);
    try {
      const res = await axios.post(`${API}/settings/smtp/test`, {
        smtp: { ...smtp, port: Number(smtp.port) || 587 },
        to_email: testEmail,
      });
      toast.success(res.data.message || "E-mail de teste enviado!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha no teste SMTP");
    } finally {
      setTestingSmtp(false);
    }
  };

  if (loading) return <div className="text-sm text-slate-500">Carregando...</div>;

  return (
    <div data-testid="settings-page" className="max-w-4xl space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">
          Configurações
        </div>
        <h1 className="heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-950">
          Integrações
        </h1>
        <p className="text-sm text-slate-600 mt-2">
          Configure como o sistema envia e-mails (via seu próprio servidor SMTP) e
          mensagens via WhatsApp.
        </p>
      </div>

      <Tabs defaultValue="empresa" value={activeTab} onValueChange={setActiveTab}>
        <TabsList data-testid="settings-tabs">
          <TabsTrigger value="empresa" data-testid="settings-tab-empresa">
            <Building2 className="w-4 h-4 mr-2" /> Empresa
          </TabsTrigger>
          <TabsTrigger value="smtp" data-testid="settings-tab-smtp">
            <Mail className="w-4 h-4 mr-2" /> SMTP
          </TabsTrigger>
          <TabsTrigger value="synkly" data-testid="settings-tab-synkly">
            <Wifi className="w-4 h-4 mr-2" /> Synkly
          </TabsTrigger>
          <TabsTrigger value="notificacoes" data-testid="settings-tab-notificacoes">
            <Bell className="w-4 h-4 mr-2" /> Notificações
          </TabsTrigger>
          {canAccess && (
            <TabsTrigger value="acessos" data-testid="settings-tab-acessos">
              <Lock className="w-4 h-4 mr-2" /> Acessos
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="backup" data-testid="settings-tab-backup">
              <Database className="w-4 h-4 mr-2" /> Backup
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="empresa" className="space-y-5 mt-5">
          <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-5">
            <div className="flex items-start justify-between gap-6 flex-wrap">
              <div>
                <h2 className="heading text-lg font-semibold text-slate-900 mb-1">
                  Logotipo
                </h2>
                <p className="text-xs text-slate-500 max-w-md">
                  Aparece no topo da sua página pública de carreiras. PNG, JPG ou WEBP
                  (máx. 2 MB). Recomendado: fundo transparente.
                </p>
              </div>
              <div className="flex items-center gap-4">
                <div
                  data-testid="company-logo-preview"
                  className="w-20 h-20 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center overflow-hidden shrink-0"
                >
                  {user?.company_logo_url ? (
                    <img
                      src={`${PUBLIC_BASE}${user.company_logo_url}`}
                      alt="Logo da empresa"
                      className="w-full h-full object-contain p-1.5"
                    />
                  ) : (
                    <ImageIcon className="w-7 h-7 text-slate-300" strokeWidth={1.5} />
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={handleLogoSelect}
                    className="hidden"
                    data-testid="company-logo-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => logoInputRef.current?.click()}
                    disabled={uploadingLogo}
                    data-testid="upload-logo-button"
                  >
                    {uploadingLogo ? (
                      <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
                    ) : (
                      <Upload className="w-3.5 h-3.5 mr-2" />
                    )}
                    {user?.company_logo_url ? "Trocar logo" : "Enviar logo"}
                  </Button>
                  {user?.company_logo_url && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleLogoRemove}
                      disabled={uploadingLogo}
                      data-testid="remove-logo-button"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-2" />
                      Remover
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-5">
            <div>
              <h2 className="heading text-lg font-semibold text-slate-900 mb-1">
                Identidade da empresa
              </h2>
              <p className="text-xs text-slate-500">
                Esses dados aparecem na página pública de carreiras. O slug é usado
                na URL — escolha algo curto e memorável.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="company-name">Nome da empresa *</Label>
                <Input
                  id="company-name"
                  data-testid="company-name-input"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Ex: Place Logistics"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company-slug">Slug (URL)</Label>
                <div className="flex items-center gap-1.5">
                  <span className="text-sm text-slate-500 whitespace-nowrap">
                    /carreiras/
                  </span>
                  <Input
                    id="company-slug"
                    data-testid="company-slug-input"
                    value={companySlug}
                    onChange={(e) => setCompanySlug(e.target.value)}
                    placeholder="place-logistics"
                    className="font-mono text-sm"
                  />
                </div>
                <p className="text-[11px] text-slate-500">
                  Apenas letras minúsculas, números e hífens. Ajustaremos automaticamente
                  se necessário.
                </p>
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                data-testid="save-company-button"
                onClick={handleSaveCompany}
                disabled={savingCompany}
                className="bg-brand-blue hover:bg-brand-blue-dark text-white"
              >
                {savingCompany ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Salvar empresa
              </Button>
            </div>
          </div>

          {careersUrl && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-5">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-md bg-emerald-100 flex items-center justify-center shrink-0">
                  <ExternalLink className="w-4 h-4 text-emerald-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-emerald-900 mb-1">
                    Sua página pública de carreiras está pronta!
                  </div>
                  <p className="text-xs text-emerald-800 leading-relaxed mb-3">
                    Compartilhe este link nas redes sociais, no LinkedIn, em anúncios da
                    Catho ou no rodapé do site da empresa. Cada candidatura é analisada
                    automaticamente pela IA.
                  </p>
                  <div className="flex items-center gap-2 bg-white border border-emerald-200 rounded-md px-3 py-2">
                    <code
                      className="flex-1 text-xs text-slate-700 font-mono truncate"
                      data-testid="careers-url"
                    >
                      {careersUrl}
                    </code>
                    <Button
                      data-testid="copy-careers-url"
                      size="sm"
                      variant="outline"
                      onClick={copyCareers}
                      className="shrink-0 h-8"
                    >
                      {copiedCareers ? (
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                      ) : (
                        <Copy className="w-3.5 h-3.5" />
                      )}
                    </Button>
                    <Button
                      data-testid="open-careers-url"
                      size="sm"
                      variant="outline"
                      onClick={() => window.open(careersUrl, "_blank", "noopener,noreferrer")}
                      className="shrink-0 h-8"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="smtp" className="space-y-5 mt-5">
          <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-5">
            <div>
              <h2 className="heading text-lg font-semibold text-slate-900 mb-1">
                Servidor SMTP
              </h2>
              <p className="text-xs text-slate-500">
                Use suas próprias credenciais de e-mail (Gmail, Outlook, AWS SES, Zoho, ou servidor próprio).
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="smtp-host">Servidor (host) *</Label>
                <Input
                  id="smtp-host"
                  data-testid="smtp-host-input"
                  value={smtp.host}
                  onChange={(e) => updateSmtp("host", e.target.value)}
                  placeholder="smtp.gmail.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="smtp-port">Porta *</Label>
                <Input
                  id="smtp-port"
                  data-testid="smtp-port-input"
                  type="number"
                  value={smtp.port}
                  onChange={(e) => updateSmtp("port", e.target.value)}
                  placeholder="587"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="smtp-user">Usuário</Label>
                <Input
                  id="smtp-user"
                  data-testid="smtp-user-input"
                  value={smtp.username}
                  onChange={(e) => updateSmtp("username", e.target.value)}
                  placeholder="seu-usuario@dominio.com"
                  autoComplete="off"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="smtp-pass">Senha</Label>
                <Input
                  id="smtp-pass"
                  data-testid="smtp-pass-input"
                  type="password"
                  value={smtp.password}
                  onChange={(e) => updateSmtp("password", e.target.value)}
                  placeholder="••••••••"
                  autoComplete="new-password"
                />
                <p className="text-[11px] text-slate-500">
                  Armazenada criptografada. Deixe como está para manter a senha atual.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="smtp-encryption">Criptografia</Label>
                <Select
                  value={smtp.encryption}
                  onValueChange={(v) => updateSmtp("encryption", v)}
                >
                  <SelectTrigger id="smtp-encryption" data-testid="smtp-encryption-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AUTO">Automático (recomendado)</SelectItem>
                    <SelectItem value="TLS">TLS / STARTTLS (porta 587)</SelectItem>
                    <SelectItem value="SSL">SSL (porta 465)</SelectItem>
                    <SelectItem value="NONE">Nenhuma (porta 25)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-slate-500">
                  Em "Automático" detectamos pela porta: 465 → SSL · 587 → TLS · 25 → sem criptografia.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="smtp-from-email">E-mail remetente *</Label>
                <Input
                  id="smtp-from-email"
                  data-testid="smtp-from-email-input"
                  type="email"
                  value={smtp.from_email}
                  onChange={(e) => updateSmtp("from_email", e.target.value)}
                  placeholder="rh@empresa.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="smtp-from-name">Nome remetente</Label>
                <Input
                  id="smtp-from-name"
                  data-testid="smtp-from-name-input"
                  value={smtp.from_name}
                  onChange={(e) => updateSmtp("from_name", e.target.value)}
                  placeholder="RH - Empresa Exemplo"
                />
              </div>
            </div>
          </div>

          {/* Test */}
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <h3 className="heading text-base font-semibold text-slate-900 mb-1">
              Testar conexão
            </h3>
            <p className="text-xs text-slate-500 mb-4">
              Enviar um e-mail de teste para verificar se a configuração está correta.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                data-testid="smtp-test-email-input"
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="seu-email-para-teste@dominio.com"
                className="flex-1"
              />
              <Button
                data-testid="smtp-test-button"
                onClick={handleTestSmtp}
                disabled={testingSmtp}
                variant="outline"
              >
                {testingSmtp ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <TestTube className="w-4 h-4 mr-2" />
                )}
                Enviar teste
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="synkly" className="space-y-5 mt-5">
          <SynklyAccountsPanel />
        </TabsContent>

        <TabsContent value="notificacoes" className="space-y-5 mt-5">
          <NotificationPrefsPanel />
        </TabsContent>

        {canAccess && (
          <TabsContent value="acessos" className="space-y-5 mt-5">
            <div className="bg-white border border-slate-200 rounded-lg p-1.5 inline-flex gap-1">
              {canAccessProfiles && (
                <button
                  type="button"
                  onClick={() => setAccessSubTab("perfis")}
                  data-testid="acessos-subtab-perfis"
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition ${
                    accessSubTab === "perfis"
                      ? "bg-brand-blue text-white shadow-sm"
                      : "text-slate-600 hover:text-brand-blue"
                  }`}
                >
                  <Shield className="w-3.5 h-3.5" /> Perfis
                </button>
              )}
              {canAccessUsers && (
                <button
                  type="button"
                  onClick={() => setAccessSubTab("usuarios")}
                  data-testid="acessos-subtab-usuarios"
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition ${
                    accessSubTab === "usuarios"
                      ? "bg-brand-blue text-white shadow-sm"
                      : "text-slate-600 hover:text-brand-blue"
                  }`}
                >
                  <UsersIcon className="w-3.5 h-3.5" /> Usuários
                </button>
              )}
            </div>

            {accessSubTab === "perfis" && canAccessProfiles && <AccessProfilesPanel />}
            {accessSubTab === "usuarios" && canAccessUsers && <AccessUsersPanel />}
          </TabsContent>
        )}
        {isAdmin && (
          <TabsContent value="backup" className="space-y-5 mt-5">
            <DatabasePortabilityPanel />
          </TabsContent>
        )}
      </Tabs>

      <div className="flex justify-end pt-2">
        {activeTab !== "acessos" && activeTab !== "backup" && activeTab !== "synkly" && (
          <Button
            data-testid="save-settings-button"
            onClick={handleSave}
            disabled={saving}
            className="bg-brand-blue hover:bg-brand-blue-dark text-white px-5 py-2.5"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Salvar configurações
          </Button>
        )}
      </div>
    </div>
  );
};

export default SettingsPage;
